
import React, { useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import { logger } from "@/utils/logging";
import { mediaStorage } from "@/services/storage/mediaStorage";

const StudentProfileImageUpload: React.FC = () => {
  const form = useFormContext();
  const [previewUrl, setPreviewUrl] = useState<string>(form.watch("profileImage") || "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get first letter of name for avatar fallback
  const nameValue = form.watch("name") || "";
  const firstLetter = nameValue.charAt(0).toUpperCase();
  
  // Enhanced file to base64 conversion with better compression for persistence
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const result = reader.result as string;
          
          // Compress image for better storage and persistence
          try {
            const compressedImage = await mediaStorage.compressImage(result, 300); // 300KB limit
            resolve(compressedImage);
          } catch (compressionError) {
            logger.warn('Image compression failed, using original', { 
              component: 'StudentProfileImageUpload',
              error: compressionError
            });
            resolve(result);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  };
  
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          logger.error('Invalid file type. Please select an image.', { 
            component: 'StudentProfileImageUpload', 
            data: { fileType: file.type }
          });
          return;
        }
        
        // Validate file size (increased limit for better quality but with compression)
        if (file.size > 10 * 1024 * 1024) { // 10MB limit before compression
          logger.error('File too large. Please select an image under 10MB.', { 
            component: 'StudentProfileImageUpload', 
            data: { fileSize: file.size }
          });
          return;
        }
        
        // Convert to compressed base64 for reliable PWA storage and persistence
        const base64String = await convertFileToBase64(file);
        
        logger.info('Converting and compressing image for enhanced persistence', {
          component: 'StudentProfileImageUpload',
          data: {
            fileName: file.name,
            originalSize: file.size,
            compressedSize: base64String.length,
            fileType: file.type,
            compressionRatio: ((file.size - base64String.length) / file.size * 100).toFixed(2) + '%'
          }
        });
        
        setPreviewUrl(base64String);
        form.setValue("profileImage", base64String);
        
        // Reset the input value to ensure onChange fires next time
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        
      } catch (error) {
        logger.error('Error processing image file', { 
          component: 'StudentProfileImageUpload' 
        }, error as Error);
      }
    }
  };
  
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleRemoveImage = () => {
    setPreviewUrl("");
    form.setValue("profileImage", "");
  };
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <FormField
        control={form.control}
        name="profileImage"
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <FormLabel className="text-center">Profile Image</FormLabel>
            <Avatar className="h-24 w-24">
              <AvatarImage src={previewUrl || field.value} />
              <AvatarFallback className="text-2xl">{firstLetter}</AvatarFallback>
            </Avatar>
            <FormControl>
              <div className="mt-2 flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleUploadClick}
                  className="relative cursor-pointer"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Image
                  <Input
                    id="profile-image-upload"
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </Button>
                {(previewUrl || field.value) && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={handleRemoveImage}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default StudentProfileImageUpload;
